// Interactive job-stage stepper. Replaces the old status <select>
// dropdown (StatusTransitionRow) as the advance/revert control on both
// the full Job Detail page and the side drawer.
//
// Click semantics (ADJACENT-ONLY): the single next stage advances one
// step; the single previous stage reverts one step. The current stage is a
// non-actionable "you are here" marker; completed stages earlier than N-1 are
// inert and carry a "step back one at a time" tooltip (reverts are single-step,
// so you can't jump back past the one revert target). This maps 1:1 to the
// server's single-step JOB_STATUS_TRANSITIONS map — verified for all 8 statuses,
// the flow-adjacent neighbor is always the allowed advance/revert target — so no
// client-side chaining is needed. Forward/revert targets are derived from the
// map, never hardcoded, so the UI can't drift if the map changes.
//
// 'created' is prepended as a leading node (JOB_STAGE_FLOW omits it) so
// the estimated→created "redo from scratch" revert is a click.
// 'disqualified' is a SEPARATE trailing red pill (an off-ramp from
// pre-install stages, not a stage in the linear flow).

import { Fragment } from 'react';
import { Check, X } from 'lucide-react';
import {
  JOB_STAGE_FLOW,
  JOB_STATUS_TRANSITIONS,
  type JobStatus,
  type ProductLine,
} from '@gutterinv/shared';
import { STATUS_LABEL } from '../lib/labels.js';

type Stage = Exclude<JobStatus, 'disqualified'>;

// Clickable node order: implicit 'created' start + the 7 visible stages.
const CLICKABLE_FLOW: ReadonlyArray<Stage> = ['created', ...JOB_STAGE_FLOW];

/** Single forward (advance) target for a status: the next node in
 *  CLICKABLE_FLOW, iff the transition map allows it. Else null. */
export function nextStageTarget(status: JobStatus): Stage | null {
  if (status === 'disqualified') return null;
  const idx = CLICKABLE_FLOW.indexOf(status);
  const next = CLICKABLE_FLOW[idx + 1];
  if (!next) return null;
  return JOB_STATUS_TRANSITIONS[status].includes(next) ? next : null;
}

/** Single backward (revert) target: the previous node, iff allowed. */
export function prevStageTarget(status: JobStatus): Stage | null {
  if (status === 'disqualified') return null;
  const idx = CLICKABLE_FLOW.indexOf(status);
  if (idx <= 0) return null;
  const prev = CLICKABLE_FLOW[idx - 1];
  return JOB_STATUS_TRANSITIONS[status].includes(prev) ? prev : null;
}

interface Props {
  currentStatus: JobStatus;
  /** Prior status to reopen TO when currentStatus === 'disqualified'. */
  disqualifiedFromStatus: Stage | null;
  productLine?: ProductLine;
  /** True while a status mutation is in flight — disables ALL clicks so
   *  a rapid second click can't double-advance / fire from a stale status. */
  disabled?: boolean;
  /** Gate created→estimated: false blocks the Estimated pill. */
  hasEstimateItems: boolean;
  /** Gate estimated→quoted: false blocks the Quoted pill (no draft PDF). */
  hasDraftPdf?: boolean;
  /** scheduled→accepted revert blocked (install assignments exist). */
  acceptRevertBlocked?: boolean;
  /** Insulation phase-gating: target-status → reason (greys that pill). */
  blockedTargets?: Partial<Record<JobStatus, string>>;
  onTransition: (to: Stage) => void;
  onDisqualify: () => void;
  onReopen: (to: Stage) => void;
}

export function JobStageStepper({
  currentStatus,
  disqualifiedFromStatus,
  productLine,
  disabled = false,
  hasEstimateItems,
  hasDraftPdf,
  acceptRevertBlocked,
  blockedTargets,
  onTransition,
  onDisqualify,
  onReopen,
}: Props) {
  const isDisqualified = currentStatus === 'disqualified';
  const effectiveStatus: Stage = isDisqualified
    ? (disqualifiedFromStatus ?? 'created')
    : currentStatus;
  const currentIdx = CLICKABLE_FLOW.indexOf(effectiveStatus);

  const advanceTarget = isDisqualified ? null : nextStageTarget(currentStatus);
  const revertTarget = isDisqualified ? null : prevStageTarget(currentStatus);

  // Reason a given advance/revert target is blocked (pre-emptive UX; the
  // server still enforces). Null = allowed.
  const blockReasonFor = (target: Stage): string | null => {
    if (blockedTargets?.[target]) return blockedTargets[target] ?? null;
    if (target === 'estimated' && currentStatus === 'created' && !hasEstimateItems) {
      return 'Add at least one SKU to the estimate before advancing.';
    }
    if (target === 'quoted' && currentStatus === 'estimated' && hasDraftPdf === false) {
      return 'Build the quote PDF first.';
    }
    if (target === 'accepted' && currentStatus === 'scheduled' && acceptRevertBlocked) {
      return productLine === 'insulation'
        ? 'Delete install phases before reverting to Accepted.'
        : 'Remove calendar assignments before reverting to Accepted.';
    }
    return null;
  };

  // The single inline reason shown under the stepper (advance target wins
  // over revert target — advancing is the primary action).
  const inlineReason =
    (advanceTarget && blockReasonFor(advanceTarget)) ||
    (revertTarget && blockReasonFor(revertTarget)) ||
    null;

  const canDisqualify =
    !isDisqualified && JOB_STATUS_TRANSITIONS[currentStatus].includes('disqualified');

  return (
    <div className="space-y-2">
      <div className="flex items-center w-full gap-1">
        {CLICKABLE_FLOW.map((stage, i) => {
          const isDone = i <= currentIdx;
          const isAdvance = stage === advanceTarget;
          const isRevert = stage === revertTarget;
          const isReopen = isDisqualified && stage === disqualifiedFromStatus;
          // "You are here" marker (non-actionable). Excludes the reopen pill,
          // which sits at the same index in the disqualified flow.
          const isCurrent = !isReopen && i === currentIdx;
          // Completed stages BEFORE the single revert target: inert. Reverts are
          // single-step (1:1 with JOB_STATUS_TRANSITIONS), so you can't jump back
          // past N-1 — these carry a "step back one at a time" tooltip.
          const isEarlierDone = isDone && !isCurrent && !isRevert && !isReopen;

          const reason = isAdvance || isRevert ? blockReasonFor(stage) : null;
          const actionable =
            !disabled && ((isAdvance || isRevert) && !reason ? true : isReopen);

          const onClick = () => {
            if (disabled) return;
            if (isReopen) onReopen(stage);
            else if ((isAdvance || isRevert) && !reason) onTransition(stage);
          };

          const title = reason
            ? reason
            : isReopen
              ? `Reopen — ${STATUS_LABEL[stage]}`
              : isAdvance
                ? `Advance to ${STATUS_LABEL[stage]}`
                : isRevert
                  ? `Back to: ${STATUS_LABEL[stage]}`
                  : isCurrent
                    ? 'Current stage — you are here'
                    : isEarlierDone
                      ? 'Step back one at a time'
                      : undefined;

          return (
            <Fragment key={stage}>
              <button
                type="button"
                onClick={onClick}
                aria-disabled={!actionable}
                aria-current={isCurrent ? 'step' : undefined}
                title={title}
                className={
                  // Variant A "enhanced pills" re-skin — same compact metrics
                  // (rounded-full px-3 py-1 text-xs); only color/state/icon change.
                  'relative inline-flex items-center gap-1 shrink-0 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-all ' +
                  // Pop-out ONLY on the two adjacent actionable stages (revert/advance)
                  // and the reopen pill. Locked-past, current, future never pop.
                  (actionable
                    ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:z-10 '
                    : 'cursor-default ') +
                  (isCurrent
                    ? // current: blue fill + blue outline + dot, no "you are here" text
                      'bg-primary text-white ring-2 ring-offset-1 ring-primary font-semibold'
                    : isReopen
                      ? 'bg-primary text-white ring-2 ring-offset-1 ring-primary/50 hover:bg-primary/90'
                      : isRevert
                        ? // immediately-previous (revert target): STRONG green + ✓
                          reason
                            ? 'bg-success/60 text-success-foreground opacity-70 cursor-not-allowed'
                            : 'bg-success text-success-foreground hover:bg-success/90'
                        : isEarlierDone
                          ? // earlier locked-completed: very light green tint + muted text/✓
                            // (same muted-foreground as the future stages), inert
                            'bg-success/10 text-muted-foreground'
                          : isAdvance
                            ? // next (advance target): red-outline on white, bare name
                              reason
                                ? 'bg-secondary text-muted-foreground border-2 border-border opacity-60 cursor-not-allowed'
                                : 'bg-card text-destructive border-2 border-destructive font-semibold hover:bg-destructive/10'
                            : // future non-adjacent: muted grey, inert
                              'bg-secondary text-muted-foreground')
                }
              >
                {isCurrent ? (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                ) : isRevert || isEarlierDone ? (
                  <Check className="h-3 w-3 shrink-0" strokeWidth={3} />
                ) : null}
                <span>{STATUS_LABEL[stage]}</span>
              </button>
              {i < CLICKABLE_FLOW.length - 1 && (
                <div
                  className={'h-0.5 flex-1 min-w-[8px] ' + (isDone ? 'bg-success/60' : 'bg-border')}
                />
              )}
            </Fragment>
          );
        })}

        {/* Trailing Disqualified pill (off-ramp / terminal). Joined with the same
            flex-1 connector as the inter-stage gaps so spacing is uniform end-to-end
            (no special divider). Grey — not part of the green progression. */}
        <div className="h-0.5 flex-1 min-w-[8px] bg-border" />
        <button
          type="button"
          onClick={() => {
            if (!disabled && canDisqualify) onDisqualify();
          }}
          aria-disabled={!(canDisqualify && !disabled)}
          title={
            isDisqualified
              ? 'Job is disqualified'
              : canDisqualify
                ? 'Disqualify this job'
                : "Can't disqualify once work has started."
          }
          className={
            // Separate destructive pill with a leading ✕. Same compact metrics.
            // Pops on hover ONLY when selectable (same actionable gating as the
            // stage pills); blocked/terminal states don't pop.
            'relative inline-flex items-center gap-1 shrink-0 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap border-2 transition-all ' +
            (isDisqualified
              ? 'bg-destructive text-white border-destructive'
              : canDisqualify && !disabled
                ? 'cursor-pointer border-destructive text-destructive hover:bg-destructive/10 hover:-translate-y-0.5 hover:shadow-md hover:z-10'
                : 'cursor-default border-border text-muted-foreground opacity-60')
          }
        >
          <X className="h-3 w-3 shrink-0" strokeWidth={3} />
          {STATUS_LABEL.disqualified}
        </button>
      </div>

      {/* Helper-text block removed (Variant A) — actions are conveyed via the
          hover tooltips (button title/aria). The blocked-reason line below is
          functional validation, NOT the generic helper text, so it stays. */}
      {inlineReason && (
        <p className="text-[0.6875rem] text-destructive">{inlineReason}</p>
      )}
    </div>
  );
}
